import React, { useState } from 'react';
import Header from '../../component/layout/Header';
import Footer from '../../component/layout/Footer';
import Page from '../../component/utility/Page';
import { custTableStyles } from '../../style/color';
import axios from 'axios';
import { mainurl } from '../../Mainurl';
import { formatNestedJson } from '../../component/utility/formatNestedJson';

import { Row, Col, Card, CardBody, Button, Input, Table, Collapse } from 'reactstrap';
import {FaSearch, FaBalanceScale, FaPlus, FaMinus, FaBuilding, FaCalendarAlt, FaDollarSign} from 'react-icons/fa';

const BalanceSheet = () => {
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [dateError, setDateError] = useState('');
    const [expandedGroups, setExpandedGroups] = useState({});

    const toggleGroup = (groupKey) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupKey]: !prev[groupKey]
        }));
    };

    const validateDates = (from, to) => {
        if (from && to && new Date(from) > new Date(to)) {
            setDateError('To Date cannot be earlier than From Date');
            return false;
        }
        setDateError('');
        return true;
    };

    const handleFromDateChange = (e) => {
        const newFromDate = e.target.value;
        setFromDate(newFromDate);
        validateDates(newFromDate, toDate);
    };

    const handleToDateChange = (e) => {
        const newToDate = e.target.value;
        setToDate(newToDate);
        validateDates(fromDate, newToDate);
    };

    const fetchBalanceSheet = () => {
        setLoading(true);
        setReportData(null);
        
        let postData = [{
            "org_id": sessionStorage.getItem('org_id'), 
            "user_id": sessionStorage.getItem('user_id'),
            "from_date": fromDate,
            "to_date": toDate
        }];
        
        axios({
            method: 'post',
            url: mainurl + 'Accounts/bl2',
            data: postData,
            headers: { 'Content-Type': 'application/JSON', }
        })
            .then(function (response) {
                console.log('Balance Sheet API Response:', response.data);
                
                if (response.data && response.data.length > 0) {
                    try {
                        // Use formatNestedJson to parse the nested JSON
                        const parsedData = formatNestedJson(response.data, false);
                        console.log('Parsed Balance Sheet Data:', parsedData);
                        console.log('Assets Section:', parsedData[0]?.nested_balance_sheet?.structure?.assets_section);
                        console.log('Assets Groups:', parsedData[0]?.nested_balance_sheet?.structure?.assets_section?.groups);
                        
                        if (parsedData[0] && parsedData[0].nested_balance_sheet) {
                            setReportData(parsedData[0].nested_balance_sheet);
                        } else {
                            console.error('Invalid data structure');
                            setReportData(null);
                        }
                    } catch (error) {
                        console.error('Error parsing nested balance sheet:', error);
                        setReportData(null);
                    }
                } else {
                    console.log('No data received');
                    setReportData(null);
                }
                setLoading(false);
            })
            .catch(error => {
                console.error('Balance Sheet API Error:', error);
                setLoading(false);
                setReportData(null);
            });
    };

    const search_handler = () => {
        // Validate dates before making API call
        if (!fromDate || !toDate) {
            setDateError('Please select both From Date and To Date');
            return;
        }
        
        if (!validateDates(fromDate, toDate)) {
            return;
        }
        
        fetchBalanceSheet();
    };

    const reset_handler = () => {
        setFromDate('');
        setToDate('');
        setReportData(null);
        setDateError('');
        setExpandedGroups({});
    };

    const formatCurrency = (amount) => {
        if (!amount) return '₹ 0.00';
        return `₹ ${Math.abs(parseFloat(amount)).toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`;
    };

    // Helper function to calculate total group amount including all children
    const calculateGroupTotalWithChildren = (group) => {
        if (!group) return 0;
        
        // Start with the group's own total (from direct accounts)
        let total = parseFloat(group.group_total || 0);
        
        // Add totals from all child groups recursively
        if (group.children && Array.isArray(group.children)) {
            group.children.forEach(childGroup => {
                total += calculateGroupTotalWithChildren(childGroup);
            });
        }
        
        return total;
    };

    const renderAccountGroup = (group, sectionType, level = 0) => {
        if (!group || group.account_count === 0) return null;
        
        const groupKey = `${sectionType}_${group.group_id}`;
        const isExpanded = expandedGroups[groupKey];
        const hasAccounts = group.accounts && group.accounts.length > 0 && group.accounts.some(acc => acc.account_id);
        const hasChildren = group.children && group.children.length > 0;
        
        // Calculate the total amount including all children
        const totalWithChildren = calculateGroupTotalWithChildren(group);
        
        // Calculate indentation based on nesting level
        const baseIndent = 20;
        const levelIndent = level * 20;
        const totalIndent = baseIndent + levelIndent;
        
        return (
            <React.Fragment key={groupKey}>
                <tr 
                    style={{ 
                        backgroundColor: level === 0 ? '#e9ecef' : level === 1 ? '#f1f3f4' : '#f8f9fa',
                        cursor: (hasAccounts || hasChildren) ? 'pointer' : 'default'
                    }}
                    onClick={(hasAccounts || hasChildren) ? () => toggleGroup(groupKey) : undefined}
                >
                    <td style={{ 
                        paddingLeft: `${totalIndent}px`,
                        fontWeight: level === 0 ? 'bold' : level === 1 ? '600' : 'normal',
                        borderLeft: level > 0 ? '2px solid #dee2e6' : 'none'
                    }}>
                        {(hasAccounts || hasChildren) && (
                            <span className="me-2">
                                {isExpanded ? <FaMinus className="text-danger" size={12} /> : <FaPlus className="text-success" size={12} />}
                            </span>
                        )}
                        {group.group_name}
                        <small className="ms-2 text-muted">({group.account_count} accounts)</small>
                    </td>
                    <td style={{ 
                        textAlign: 'right', 
                        fontWeight: level === 0 ? 'bold' : level === 1 ? '600' : 'normal',
                        color: level === 0 ? '#495057' : '#6c757d'
                    }}>
                        <span className={(() => {
                            // Color code group totals based on section type and amount
                            if (level === 0) {
                                if (sectionType === 'assets') {
                                    return totalWithChildren >= 0 ? 'text-success' : 'text-danger';
                                } else if (sectionType === 'liabilities' || sectionType === 'equity') {
                                    return totalWithChildren >= 0 ? 'text-primary' : 'text-danger';
                                }
                            }
                            return totalWithChildren >= 0 ? 'text-dark' : 'text-danger';
                        })()}>
                            {formatCurrency(totalWithChildren)}
                        </span>
                        {hasChildren && level === 0 && (
                            <><br />
                            <small className="text-muted" style={{ fontSize: '0.75em' }}>
                                Direct: {formatCurrency(group.group_total)}
                            </small></>
                        )}
                    </td>
                </tr>
                
                {hasAccounts && (
                    <Collapse isOpen={isExpanded}>
                        {group.accounts.filter(account => account.account_id).map((account) => (
                            <tr key={account.account_id} style={{ backgroundColor: '#f8f9fa' }}>
                                <td style={{ 
                                    paddingLeft: `${totalIndent + 20}px`,
                                    fontSize: '0.9em',
                                    borderLeft: '2px solid #dee2e6'
                                }}>
                                    <strong className="text-primary">{account.account_code}</strong> - {account.account_name}
                                    <br />
                                    <small className="text-muted">
                                        <span className="text-danger">Debits: {formatCurrency(account.total_debits)}</span> | 
                                        <span className="text-success">Credits: {formatCurrency(account.total_credits)}</span>
                                    </small>
                                </td>
                                <td style={{ 
                                    textAlign: 'right',
                                    fontSize: '0.9em',
                                    color: '#6c757d'
                                }}>
                                    <strong className={(() => {
                                        const balance = parseFloat(account.current_balance || 0);
                                        const debits = parseFloat(account.total_debits || 0);
                                        const credits = parseFloat(account.total_credits || 0);
                                        
                                        if (sectionType === 'assets') {
                                            // For assets, debit balances are normal (positive), credit balances are unusual
                                            return debits > credits ? 'text-success' : 'text-danger';
                                        } else if (sectionType === 'liabilities' || sectionType === 'equity') {
                                            // For liabilities and equity, credit balances are normal (positive)
                                            return credits > debits ? 'text-success' : 'text-danger';
                                        }
                                        return balance >= 0 ? 'text-success' : 'text-danger';
                                    })()}>
                                        {formatCurrency(account.current_balance)}
                                    </strong>
                                </td>
                            </tr>
                        ))}
                    </Collapse>
                )}
                
                {hasChildren && isExpanded && (
                    <>
                        {group.children.map(childGroup => renderAccountGroup(childGroup, sectionType, level + 1))}
                    </>
                )}
            </React.Fragment>
        );
    };

    return (
        <>
            <Header />
            <Page title='Balance Sheet' breadcrumbs='Home / Financial / Balance Sheet'>
                <Row className='p-3'>
                    <Col xs={12}>
                        <Card className='mb-3' color="primary" outline>
                            <CardBody>
                                <h6 className='mb-3'>
                                    <FaBalanceScale className='me-2' />
                                    Search Criteria
                                </h6>
                                <Row>
                                    <Col md={4}>
                                        <label className='form-label'>From Date</label>
                                        <Input 
                                            type='date' 
                                            value={fromDate} 
                                            onChange={handleFromDateChange}
                                            max={toDate || undefined}
                                            className={dateError ? 'is-invalid' : ''}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <label className='form-label'>To Date</label>
                                        <Input 
                                            type='date' 
                                            value={toDate} 
                                            onChange={handleToDateChange}
                                            min={fromDate || undefined}
                                            className={dateError ? 'is-invalid' : ''}
                                        />
                                    </Col>
                                    <Col md={4} className='d-flex align-items-end'>
                                        <Button 
                                            className='text-white me-2' 
                                            color='primary' 
                                            size="sm" 
                                            onClick={search_handler}
                                            disabled={!!dateError || !fromDate || !toDate}
                                        > 
                                            <FaSearch className='me-1' size={16} /> Generate Report
                                        </Button>
                                        <Button className='text-white' color='secondary' size="sm" onClick={reset_handler}> 
                                            Reset
                                        </Button>
                                    </Col>
                                </Row>
                                {dateError && (
                                    <Row className='mt-2'>
                                        <Col xs={12}>
                                            <div className='text-danger small'>
                                                <i className="fa fa-exclamation-triangle me-1"></i>
                                                {dateError}
                                            </div>
                                        </Col>
                                    </Row>
                                )}
                            </CardBody>
                        </Card>

                        {/* Balance Sheet Content */}
                        {reportData ? (
                            <>
                                {/* Side by Side Layout - Assets & Liabilities/Equity */}
                                <Row className='g-4'>
                                    {/* Assets Section - Left Column */}
                                    <Col lg={6}>
                                        <Card className='h-100 shadow-sm border-0'>
                                            <div className='card-header bg-white border-bottom py-3'>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <h5 className='mb-0 text-dark fw-bold'>
                                                        {reportData.structure?.assets_section?.section_name || 'ASSETS'}
                                                    </h5>
                                                    <span className='fw-bold fs-6 text-success'>
                                                        {formatCurrency(reportData.structure?.assets_section?.section_total || 0)}
                                                    </span>
                                                </div>
                                            </div>
                                            <CardBody className='p-0'>
                                                <div className='table-responsive'>
                                                    <Table hover size="sm" className="mb-0">
                                                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                                                            <tr>
                                                                <th style={{ borderTop: 'none', fontWeight: 'bold', padding: '12px 20px' }}>Account</th>
                                                                <th style={{ borderTop: 'none', textAlign: 'right', fontWeight: 'bold', padding: '12px 20px' }}>Amount</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {/* First render groups if they exist */}
                                                            {reportData.structure?.assets_section?.groups?.length > 0 ? (
                                                                reportData.structure.assets_section.groups.map(group => 
                                                                    renderAccountGroup(group, 'assets')
                                                                )
                                                            ) : null}
                                                            
                                                            {/* If no groups but section has accounts at section level, render them directly */}
                                                            {(!reportData.structure?.assets_section?.groups || reportData.structure.assets_section.groups.length === 0) && 
                                                             reportData.structure?.assets_section?.accounts?.length > 0 ? (
                                                                reportData.structure.assets_section.accounts.map((account) => (
                                                                    <tr key={account.account_id} style={{ backgroundColor: '#f8f9fa' }}>
                                                                        <td style={{ 
                                                                            paddingLeft: '20px',
                                                                            fontSize: '0.9em'
                                                                        }}>
                                                                            <strong className="text-primary">{account.account_code}</strong> - {account.account_name}
                                                                            <br />
                                                                            <small className="text-muted">
                                                                                <span className="text-danger">Debits: {formatCurrency(account.total_debits)}</span> | 
                                                                                <span className="text-success">Credits: {formatCurrency(account.total_credits)}</span>
                                                                            </small>
                                                                        </td>
                                                                        <td style={{ 
                                                                            textAlign: 'right',
                                                                            fontSize: '0.9em',
                                                                            color: '#6c757d'
                                                                        }}>
                                                                            <strong className={(() => {
                                                                                const balance = parseFloat(account.current_balance || 0);
                                                                                const debits = parseFloat(account.total_debits || 0);
                                                                                const credits = parseFloat(account.total_credits || 0);
                                                                                return debits > credits ? 'text-success' : 'text-danger';
                                                                            })()}>
                                                                                {formatCurrency(account.current_balance)}
                                                                            </strong>
                                                                        </td>
                                                                    </tr>
                                                                ))
                                                            ) : null}
                                                            
                                                            {/* Show "no data" message only if both groups and accounts are empty */}
                                                            {(!reportData.structure?.assets_section?.groups || reportData.structure.assets_section.groups.length === 0) && 
                                                             (!reportData.structure?.assets_section?.accounts || reportData.structure.assets_section.accounts.length === 0) ? (
                                                                <tr>
                                                                    <td colSpan="2" className="text-center text-muted py-4">
                                                                        No asset data available
                                                                    </td>
                                                                </tr>
                                                            ) : null}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </CardBody>
                                        </Card>
                                    </Col>

                                    {/* Liabilities & Equity Section - Right Column */}
                                    <Col lg={6}>
                                        <Card className='h-100 shadow-sm border-0'>
                                            <div className='card-header bg-white border-bottom py-3'>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <h5 className='mb-0 text-dark fw-bold'>LIABILITIES & EQUITY</h5>
                                                    <span className={`fw-bold fs-6 ${(() => {
                                                        const liabilitiesTotal = parseFloat(reportData.structure?.liabilities_section?.section_total || 0);
                                                        const equityTotal = parseFloat(reportData.structure?.equity_section?.section_total || 0);
                                                        const combinedTotal = liabilitiesTotal + equityTotal;
                                                        
                                                        if (combinedTotal === 0) return 'text-warning'; // Yellow for neutral/zero
                                                        return combinedTotal > 0 ? 'text-success' : 'text-danger'; // Green for positive, red for negative
                                                    })()}`}>
                                                        {(() => {
                                                            const liabilitiesTotal = parseFloat(reportData.structure?.liabilities_section?.section_total || 0);
                                                            const equityTotal = parseFloat(reportData.structure?.equity_section?.section_total || 0);
                                                            const combinedTotal = liabilitiesTotal + equityTotal;
                                                            return formatCurrency(combinedTotal);
                                                        })()}
                                                    </span>
                                                </div>
                                            </div>
                                            <CardBody className='p-0'>
                                                <div className='table-responsive'>
                                                    <Table hover size="sm" className="mb-0">
                                                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                                                            <tr>
                                                                <th style={{ borderTop: 'none', fontWeight: 'bold', padding: '12px 20px' }}>Account</th>
                                                                <th style={{ borderTop: 'none', textAlign: 'right', fontWeight: 'bold', padding: '12px 20px' }}>Amount</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {/* Liabilities Section */}
                                                            <tr style={{ backgroundColor: '#e3f2fd' }}>
                                                                <td style={{ 
                                                                    paddingLeft: '15px',
                                                                    fontWeight: 'bold',
                                                                    fontSize: '1.1em',
                                                                    color: '#1976d2'
                                                                }}>
                                                                    {reportData.structure?.liabilities_section?.section_name || 'LIABILITIES'}
                                                                </td>
                                                                <td style={{ 
                                                                    textAlign: 'right', 
                                                                    fontWeight: 'bold',
                                                                    fontSize: '1.1em',
                                                                    color: '#1976d2'
                                                                }}>
                                                                    <span className="text-warning">
                                                                        {formatCurrency(reportData.structure?.liabilities_section?.section_total || 0)}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                            {/* Liabilities Groups */}
                                                            {reportData.structure?.liabilities_section?.groups?.length > 0 ? (
                                                                reportData.structure.liabilities_section.groups.map(group => 
                                                                    renderAccountGroup(group, 'liabilities')
                                                                )
                                                            ) : null}
                                                            
                                                            {/* If no liability groups but section has accounts at section level, render them directly */}
                                                            {(!reportData.structure?.liabilities_section?.groups || reportData.structure.liabilities_section.groups.length === 0) && 
                                                             reportData.structure?.liabilities_section?.accounts?.length > 0 ? (
                                                                reportData.structure.liabilities_section.accounts.map((account) => (
                                                                    <tr key={account.account_id} style={{ backgroundColor: '#f8f9fa' }}>
                                                                        <td style={{ 
                                                                            paddingLeft: '30px',
                                                                            fontSize: '0.9em',
                                                                            borderLeft: '2px solid #dee2e6'
                                                                        }}>
                                                                            <strong className="text-primary">{account.account_code}</strong> - {account.account_name}
                                                                            <br />
                                                                            <small className="text-muted">
                                                                                <span className="text-danger">Debits: {formatCurrency(account.total_debits)}</span> | 
                                                                                <span className="text-success">Credits: {formatCurrency(account.total_credits)}</span>
                                                                            </small>
                                                                        </td>
                                                                        <td style={{ 
                                                                            textAlign: 'right',
                                                                            fontSize: '0.9em',
                                                                            color: '#6c757d'
                                                                        }}>
                                                                            <strong className={(() => {
                                                                                const balance = parseFloat(account.current_balance || 0);
                                                                                const debits = parseFloat(account.total_debits || 0);
                                                                                const credits = parseFloat(account.total_credits || 0);
                                                                                return credits > debits ? 'text-success' : 'text-danger';
                                                                            })()}>
                                                                                {formatCurrency(account.current_balance)}
                                                                            </strong>
                                                                        </td>
                                                                    </tr>
                                                                ))
                                                            ) : null}
                                                            
                                                            {/* Show "no data" message only if both groups and accounts are empty */}
                                                            {(!reportData.structure?.liabilities_section?.groups || reportData.structure.liabilities_section.groups.length === 0) && 
                                                             (!reportData.structure?.liabilities_section?.accounts || reportData.structure.liabilities_section.accounts.length === 0) ? (
                                                                <tr>
                                                                    <td colSpan="2" className="text-center text-muted py-2" style={{ paddingLeft: '30px' }}>
                                                                        No liability data available
                                                                    </td>
                                                                </tr>
                                                            ) : null}
                                                            
                                                            {/* Spacer row */}
                                                            <tr>
                                                                <td colSpan="2" style={{ height: '20px', borderBottom: 'none' }}></td>
                                                            </tr>
                                                            
                                                            {/* Equity Section */}
                                                            <tr style={{ backgroundColor: '#e8f5e8' }}>
                                                                <td style={{ 
                                                                    paddingLeft: '15px',
                                                                    fontWeight: 'bold',
                                                                    fontSize: '1.1em',
                                                                    color: '#2e7d32'
                                                                }}>
                                                                    {reportData.structure?.equity_section?.section_name || 'EQUITY'}
                                                                </td>
                                                                <td style={{ 
                                                                    textAlign: 'right', 
                                                                    fontWeight: 'bold',
                                                                    fontSize: '1.1em',
                                                                    color: '#2e7d32'
                                                                }}>
                                                                    <span className="text-info">
                                                                        {formatCurrency(reportData.structure?.equity_section?.section_total || 0)}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                            {/* Equity Groups */}
                                                            {reportData.structure?.equity_section?.groups?.length > 0 ? (
                                                                reportData.structure.equity_section.groups.map(group => 
                                                                    renderAccountGroup(group, 'equity')
                                                                )
                                                            ) : null}
                                                            
                                                            {/* If no equity groups but section has accounts at section level, render them directly */}
                                                            {(!reportData.structure?.equity_section?.groups || reportData.structure.equity_section.groups.length === 0) && 
                                                             reportData.structure?.equity_section?.accounts?.length > 0 ? (
                                                                reportData.structure.equity_section.accounts.map((account) => (
                                                                    <tr key={account.account_id} style={{ backgroundColor: '#f8f9fa' }}>
                                                                        <td style={{ 
                                                                            paddingLeft: '30px',
                                                                            fontSize: '0.9em',
                                                                            borderLeft: '2px solid #dee2e6'
                                                                        }}>
                                                                            <strong className="text-primary">{account.account_code}</strong> - {account.account_name}
                                                                            <br />
                                                                            <small className="text-muted">
                                                                                <span className="text-danger">Debits: {formatCurrency(account.total_debits)}</span> | 
                                                                                <span className="text-success">Credits: {formatCurrency(account.total_credits)}</span>
                                                                            </small>
                                                                        </td>
                                                                        <td style={{ 
                                                                            textAlign: 'right',
                                                                            fontSize: '0.9em',
                                                                            color: '#6c757d'
                                                                        }}>
                                                                            <strong className={(() => {
                                                                                const balance = parseFloat(account.current_balance || 0);
                                                                                const debits = parseFloat(account.total_debits || 0);
                                                                                const credits = parseFloat(account.total_credits || 0);
                                                                                return credits > debits ? 'text-success' : 'text-danger';
                                                                            })()}>
                                                                                {formatCurrency(account.current_balance)}
                                                                            </strong>
                                                                        </td>
                                                                    </tr>
                                                                ))
                                                            ) : null}
                                                            
                                                            {/* Show "no data" message only if both groups and accounts are empty */}
                                                            {(!reportData.structure?.equity_section?.groups || reportData.structure.equity_section.groups.length === 0) && 
                                                             (!reportData.structure?.equity_section?.accounts || reportData.structure.equity_section.accounts.length === 0) ? (
                                                                <tr>
                                                                    <td colSpan="2" className="text-center text-muted py-2" style={{ paddingLeft: '30px' }}>
                                                                        No equity data available
                                                                    </td>
                                                                </tr>
                                                            ) : null}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                </Row>

                                {/* Balance Sheet Summary - Bottom Section */}
                                {reportData.structure && (
                                    <Card className='mt-4 shadow-sm border-0'>
                                        <CardBody className='p-4'>
                                            <h5 className='mb-4 text-dark fw-bold'>
                                                Balance Sheet Summary
                                                <small className="ms-2 text-muted">
                                                    (As of: {reportData.as_of_date})
                                                </small>
                                            </h5>
                                            <Row className='g-3'>
                                                <Col md={3}>
                                                    <div className='p-3 bg-light rounded'>
                                                        <h6 className='text-success fw-bold'>Total Assets</h6>
                                                        <h4 className='text-success mb-0'>
                                                            {formatCurrency(reportData.structure.summary.total_assets)}
                                                        </h4>
                                                        <small className="text-muted">
                                                            ({reportData.structure.summary.account_counts.asset_accounts} accounts)
                                                        </small>
                                                    </div>
                                                </Col>
                                                <Col md={3}>
                                                    <div className='p-3 bg-light rounded'>
                                                        <h6 className='text-warning fw-bold'>Total Liabilities</h6>
                                                        <h4 className='text-warning mb-0'>
                                                            {formatCurrency(reportData.structure.summary.total_liabilities)}
                                                        </h4>
                                                        <small className="text-muted">
                                                            ({reportData.structure.summary.account_counts.liability_accounts} accounts)
                                                        </small>
                                                    </div>
                                                </Col>
                                                <Col md={3}>
                                                    <div className='p-3 bg-light rounded'>
                                                        <h6 className='text-info fw-bold'>Total Equity</h6>
                                                        <h4 className='text-info mb-0'>
                                                            {formatCurrency(reportData.structure.summary.total_equity)}
                                                        </h4>
                                                        <small className="text-muted">
                                                            ({reportData.structure.summary.account_counts.equity_accounts} accounts)
                                                        </small>
                                                    </div>
                                                </Col>
                                                <Col md={3}>
                                                    <div className={`p-3 rounded ${
                                                        reportData.structure.summary.balance_check === 'BALANCED' 
                                                            ? 'bg-success text-white' 
                                                            : 'bg-danger text-white'
                                                    }`}>
                                                        <h6 className="fw-bold">Balance Status</h6>
                                                        <h4 className="mb-0">
                                                            {reportData.structure.summary.balance_check === 'BALANCED' 
                                                                ? 'Balanced ✓' 
                                                                : 'Not Balanced ✗'
                                                            }
                                                        </h4>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </CardBody>
                                    </Card>
                                )}
                            </>
                        ) : (
                            <Card className='mb-3 shadow-sm border-0'>
                                <CardBody className="text-center py-5">
                                    {loading ? (
                                        <>
                                            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                                            <h5 className="text-muted">Loading Balance Sheet...</h5>
                                        </>
                                    ) : (
                                        <>
                                            <FaBalanceScale size={48} className="text-muted mb-3" />
                                            <h5 className="text-muted">No Balance Sheet Data Available</h5>
                                            <p className="text-muted">
                                                Please select a date range and click "Generate Report" to view the Balance Sheet.
                                            </p>
                                        </>
                                    )}
                                </CardBody>
                            </Card>
                        )}
                    </Col>
                </Row>
            </Page>
            <Footer />
        </>
    );
};

export default BalanceSheet;
